import type { ToggleOption } from '@/components'
import { FAIcon, ModuleWrapper, Toggles } from '@/components'
import { FullPageLoader } from '@/components_deprecated'
import { PageType } from '@/modules/common/consts'
import { count } from '@/utils'
import { endOfWeek, startOfWeek } from '@/utils/date-fns'
import type { useLeaderGroups } from '@/utils/hooks'
import {
  useAdmin,
  useFormQueryField,
  useFormWatch,
  useLeaderGroupsSuspense,
  useNodes,
  useOnErrorAuto,
  useParseQueryState,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm as useFormOld, useTranslate } from '@ur-solutions/react-hooks'
import { addMonths, endOfMonth, startOfMonth } from 'date-fns'
import type { VariablesOf } from 'gql.tada'
import queryString from 'query-string'
import React, {
  Activity,
  Suspense,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import type { UseFormReturn } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import { useLocation } from 'react-router'
import { AbsenceDialog, AbsenceRequests, AbsencesTable } from './components'
import type { AbsencesFiltersForm } from './components/absences-filters'
import {
  AbsencesFilters,
  AbsencesFiltersSchema,
} from './components/absences-filters'
import { AbsencesCalendar } from './components/AbsencesCalendar'
import { USER_ABSENCES_QUERY } from './queries'
import type { AbsenceFilterForm } from './types'
import type { AbsenceTypeNode } from './types.graphql'

interface InitialFilterParams {
  userId: string
  date: Date
}

function useInitialFilters(
  defaultLeaderGroup: ReturnType<typeof useLeaderGroups>['defaultLeaderGroup']
): AbsencesFiltersForm {
  const me = useUser()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()

  const parsed = useParseQueryState(AbsencesFiltersSchema, 'view')

  const dateStart = startOfMonth(addMonths(new Date(), -1))
  const dateEnd = endOfMonth(new Date())

  return {
    view: parsed.view ?? 'table',
    users: hasPermissions(PERMISSIONS.timesheets.view.othersabsence)
      ? []
      : [me.id],
    absenceType: '',
    requestStatus: '',
    dateRange: {
      from: dateStart,
      to: dateEnd,
    },
    byCreatedDate: false,
    month: new Date(),
    leaderGroup: !admin ? defaultLeaderGroup?.id ?? '' : '',
  }
}

function useAbsencesQueryVariables(
  form: UseFormReturn<AbsencesFiltersForm>
): VariablesOf<typeof USER_ABSENCES_QUERY> {
  const view = useFormWatch(form, 'view')

  const users = useFormWatch(form, 'users')
  const absenceType = useFormWatch(form, 'absenceType') || undefined
  const requestStatus = useFormWatch(form, 'requestStatus') || undefined
  const leaderGroup = useFormWatch(form, 'leaderGroup') || undefined
  const byCreatedDate = useFormWatch(form, 'byCreatedDate')

  const { from, to } = useFormWatch(form, 'dateRange')
  const month = useFormWatch(form, 'month')

  const variables: VariablesOf<typeof USER_ABSENCES_QUERY> = {
    users,
    absenceType,
    requestStatus,
    leaderGroup,
  }

  const start: string | undefined = from?.toISOString()
  const end: string | undefined = to?.toISOString()

  if (view === 'calendar') {
    variables.datetimeStartGte = startOfWeek(startOfMonth(month)).toISOString()
    variables.datetimeStartLte = endOfWeek(endOfMonth(month)).toISOString()
  } else {
    if (byCreatedDate) {
      variables.createdAtGte = start
      variables.createdAtLte = end
    } else {
      variables.datetimeStartGte = start
      variables.datetimeStartLte = end
    }
  }

  return variables
}

interface AbsencesProps {}

const AbsencesInner: React.FC<AbsencesProps> = () => {
  const translations = useTranslate({
    absences: 'common.absences',
    registerAbsence: 'absences.absence-dialog.add',
  })

  const [selectedAbsenceType, setSelectedAbsenceType] = useState<Pick<
    AbsenceTypeNode,
    'id' | 'name'
  > | null>(null)

  const me = useUser()
  const isAdmin = useAdmin()
  const location = useLocation()
  const { hasPermissions } = usePermissions()

  const canManageRequests = hasPermissions(
    PERMISSIONS.timesheets.change.othersabsence
  )
  const canAddOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.othersabsence,
    false
  )

  const initialFilter = useMemo<InitialFilterParams>(() => {
    const search = queryString.parse(location.search)
    const date = (search.date as string) || null
    const userId = (search.user as string) || null
    return {
      date: date ? new Date(date) : new Date(),
      userId: hasPermissions(PERMISSIONS.timesheets.change.absence)
        ? userId ?? me.id
        : me.id,
    }
  }, [hasPermissions, location.search, me.id])

  const [initialLoaded, setInitialLoaded] = useState(false)

  const leaderGroups = useLeaderGroupsSuspense()

  const form = useForm<AbsencesFiltersForm>({
    resolver: zodResolver(AbsencesFiltersSchema),
    defaultValues: useInitialFilters(leaderGroups.defaultLeaderGroup),
  })

  useFormQueryField(form, 'view')

  const {
    formValues: filter,
    updateForm,
    updateInitialValues,
  } = useFormOld<AbsenceFilterForm>({
    values: {
      users: [],
      user: undefined,
      month: initialFilter.date,
      date: [initialFilter.date, initialFilter.date],
      orderBy: 'start',
      isDate: false,
      isDateStart: false,
      allUsers: false,
      requestStatus: undefined,
      view: 'table',
      salaryMode: false,
      leaderGroup: null,
      leaderGroupUsers: [],
    },
    config: {
      storage: {
        storeFormState: true,
        storeFormStateName: 'absence-controls',
        retrieveFormStateOnMount: true,
        transformValues: {
          month: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          salaryMode: value => (typeof value === 'boolean' ? value : false),
        },
      },
    },
  })

  const filterView = useFormWatch(form, 'view')
  const filterMonth = useFormWatch(form, 'month')

  const { datetimeEndGte, datetimeStartLt } = (() => {
    const startOfMonthDate = startOfMonth(filterMonth)
    const endOfMonthDate = addMonths(startOfMonthDate, 1)

    const datetimeStartLt =
      filterView === 'table' ? startOfMonthDate : startOfWeek(startOfMonthDate)
    const datetimeEndGte =
      filterView === 'table' ? endOfMonthDate : endOfWeek(endOfMonthDate)

    return {
      datetimeStartLt,
      datetimeEndGte,
    }
  })()

  const onErrorAuto = useOnErrorAuto()
  const queryVariables = useAbsencesQueryVariables(form)

  const { data, loading } = useQuery(USER_ABSENCES_QUERY, {
    skip: filterView === 'requests',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    variables: queryVariables,
    onError: onErrorAuto(),
  })

  const absences = useNodes(data?.allAbsences, absence => {
    if (!filter.leaderGroupUsers.length) return true
    return filter.leaderGroupUsers.includes(absence.user.id)
  })

  const viewToggleOptions: ToggleOption<AbsencesFiltersForm['view']>[] = [
    {
      value: 'table',
      label: <FAIcon icon="list" />,
    },
    {
      value: 'requests',
      label: <FAIcon icon="memo-circle-check" />,
      hide: !canManageRequests,
      badge: isMobile ? null : data?.pendingAbsenceCount || null,
    },
    {
      value: 'calendar',
      label: <FAIcon icon="calendar-alt" />,
      hide: isMobile,
    },
  ]

  const showViewToggle = count(viewToggleOptions, opt => !opt.hide) > 1

  const handleSetInitialLoaded = useEffectEvent(() => {
    setInitialLoaded(true)
  })

  useEffect(() => {
    if (initialFilter.date !== filter.date[0] && !initialLoaded) {
      updateForm({ date: [initialFilter.date, initialFilter.date] })
      handleSetInitialLoaded()
    }
  }, [filter.date, initialFilter.date, initialLoaded, updateForm])

  // TODO: Add to multiple pages if logic is suitable
  useEffect(() => {
    if (
      leaderGroups.defaultLeaderGroup &&
      !filter.leaderGroup &&
      leaderGroups.myLeaderGroups.length === 1 &&
      !isAdmin
    ) {
      updateForm({
        leaderGroup: leaderGroups.defaultLeaderGroup.id,
        leaderGroupUsers: leaderGroups.defaultLeaderGroup.users.map(
          user => user.id
        ),
      })
      updateInitialValues({ leaderGroup: leaderGroups.defaultLeaderGroup.id })
    }
  }, [
    filter.leaderGroup,
    isAdmin,
    leaderGroups.defaultLeaderGroup,
    leaderGroups.myLeaderGroups.length,
    updateForm,
    updateInitialValues,
  ])

  return (
    <ModuleWrapper
      title={translations.absences}
      page={PageType.ABSENCES}
      headerActions={<AbsenceDialog canAddForOthers={canAddOthersAbsence} />}
      headerRight={
        <Activity mode={showViewToggle ? 'visible' : 'hidden'}>
          <Controller
            control={form.control}
            name="view"
            render={({ field }) => (
              <Toggles
                {...field}
                options={viewToggleOptions}
                variant="white"
                size={isMobile ? 'md' : 'lg'}
              />
            )}
          />
        </Activity>
      }
    >
      <AbsencesFilters form={form} leaderGroups={leaderGroups} />

      <Activity mode={filterView === 'table' ? 'visible' : 'hidden'}>
        <AbsencesTable
          absences={absences}
          loading={loading}
          selectedAbsenceType={selectedAbsenceType}
          onSelectAbsenceType={absenceType => {
            setSelectedAbsenceType(absenceType)
          }}
        />
      </Activity>

      <Activity mode={filterView === 'calendar' ? 'visible' : 'hidden'}>
        <AbsencesCalendar
          startDate={datetimeStartLt}
          endDate={datetimeEndGte}
          absences={absences}
        />
      </Activity>

      <Activity mode={filterView === 'requests' ? 'visible' : 'hidden'}>
        <AbsenceRequests filter={form} />
      </Activity>
    </ModuleWrapper>
  )
}

export function Absences() {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <AbsencesInner />
    </Suspense>
  )
}
